import { HrefLink as Link } from "@/components/ui/href-link";
import { RequirePermission } from "@/hooks/shared/CheckAuth";
import { createFileRoute } from "@tanstack/react-router";
import { ArrowRight } from "lucide-react";

import { PersonAvatar } from "@/components/employees/person-avatar";
import { PageHeader } from "@/components/dashboard/page-header";
import { StatusPill, type StatusTone } from "@/components/dashboard/status-pill";
import { DeviceChangeDecisionPanel } from "@/components/device-changes/decision-panel";
import { deviceChanges as copy, nav, roles, status as statusCopy } from "@/content";
import { can } from "@/constants/permissions";
import {
  getDeviceChangeRequest,
  type DeviceChangeStatus,
} from "@/api/device-changes";
import { useQuery } from "@tanstack/react-query";
import { useFmt } from "@/hooks/shared/useFmt";
import { useSession } from "@/context/UserContext";
import { RecordNotFound } from "@/components/dashboard/record-not-found";
import { SkeletonPanel } from "@/components/dashboard/skeletons";
import { useDocumentTitle } from "@/hooks/shared/useDocumentTitle";
import { formatPhone } from "@/utils/phone";

const STATUS_TONE: Record<DeviceChangeStatus, StatusTone> = {
  pending: "attention",
  approved: "positive",
  rejected: "negative",
};

export const Route = createFileRoute("/_main/dashboard/device-changes/$id")({
  component: () => (
    <RequirePermission action="deviceChanges.read">
      <DeviceChangeDetailPage />
    </RequirePermission>
  ),
});

function DeviceChangeDetailPage() {
  useDocumentTitle(copy.requestTitle);
  const { user } = useSession();
  const { id } = Route.useParams();
  const requestId = Number(id);
  const validId = Number.isInteger(requestId) && requestId >= 1;
  const fmt = useFmt();

  const requestQuery = useQuery({
    queryKey: ["deviceChanges", "get", requestId],
    queryFn: () => getDeviceChangeRequest(requestId),
    enabled: validId,
  });
  const request = requestQuery.data;

  if (!validId || (requestQuery.isFetched && !request)) return <RecordNotFound />;
  if (!request) {
    return (
      <div className="mx-auto w-full max-w-shell px-6 py-8">
        <SkeletonPanel height="20rem" />
      </div>
    );
  }

  const roleLabel =
    request.userRole === "guard" || request.userRole === "supervisor"
      ? roles[request.userRole]
      : request.userRole;

  return (
    <div className="mx-auto w-full max-w-shell px-6 py-8">
      <PageHeader
        eyebrow={nav.groups.people}
        title={request.userName}
        lead={`${copy.requestTitle} · ${roleLabel}`}
        actions={
          <div className="flex flex-wrap items-center gap-2">
            <Link
              to="/dashboard/device-changes"
              className="inline-flex h-10 items-center gap-2 rounded-lg border border-n4 bg-n0 px-4 text-sm font-semibold text-n8 transition-colors duration-200 ease-brand hover:bg-n2"
            >
              <ArrowRight aria-hidden className="size-4" strokeWidth={1.6} />
              {copy.backToQueue}
            </Link>
            <DeviceChangeDecisionPanel
              id={request.id}
              userName={request.userName}
              status={request.status}
              canDecide={can(user.role, "deviceChanges.decide")}
            />
          </div>
        }
      />

      <div className="mt-8 max-w-3xl">
        <div className="mb-4 flex items-center gap-2.5">
          <PersonAvatar name={request.userName} photoUrl={request.photoUrl} size="md" />
          <Link
            to={`/dashboard/employees/${request.userId}`}
            className="text-sm font-semibold text-orange-700 transition-opacity duration-200 ease-brand hover:opacity-80"
          >
            {request.userName}
          </Link>
        </div>
        <dl className="grid gap-4 rounded-xl border border-n3 bg-n0 p-5 sm:grid-cols-2">
          <div>
            <dt className="text-2xs font-semibold text-n6">{copy.columnStatus}</dt>
            <dd className="mt-1">
              <StatusPill tone={STATUS_TONE[request.status]}>
                {statusCopy[request.status]}
              </StatusPill>
            </dd>
          </div>
          <div>
            <dt className="text-2xs font-semibold text-n6">{copy.columnFiled}</dt>
            <dd className="mt-1 text-sm text-n8">{fmt.dateTime(request.createdAt)}</dd>
          </div>
          <div>
            <dt className="text-2xs font-semibold text-n6">{copy.columnPhone}</dt>
            <dd className="tabular mt-1 text-sm text-n8" dir="ltr">
              {request.userPhone ?? copy.noPhone}
            </dd>
          </div>
          {request.newPhone ? (
            <div>
              <dt className="text-2xs font-semibold text-n6">{copy.newNumber}</dt>
              <dd className="tabular mt-1 text-sm font-semibold text-n9" dir="ltr">
                {formatPhone(request.newPhone) ?? request.newPhone}
              </dd>
            </div>
          ) : null}
          <div>
            <dt className="text-2xs font-semibold text-n6">{copy.columnRole}</dt>
            <dd className="mt-1 text-sm text-n8">{roleLabel}</dd>
          </div>
          <div className="sm:col-span-2">
            <dt className="text-2xs font-semibold text-n6">{copy.columnPreviousDevice}</dt>
            <dd className="mt-1 break-words text-sm text-n7" dir="ltr">
              {request.previousDeviceLabel?.trim() || request.previousDeviceId || copy.noPreviousDevice}
            </dd>
          </div>
          <div className="sm:col-span-2">
            <dt className="text-2xs font-semibold text-n6">{copy.columnNewDevice}</dt>
            <dd className="mt-1 break-words text-sm text-n8" dir="ltr">
              {request.newDeviceLabel?.trim() || request.newDeviceId}
            </dd>
          </div>
          <div className="sm:col-span-2">
            <dt className="text-2xs font-semibold text-n6">{copy.columnReason}</dt>
            <dd className="mt-1 whitespace-pre-wrap text-sm text-n8">
              {request.reason?.trim() ? request.reason : copy.noReason}
            </dd>
          </div>
        </dl>

        <section className="mt-8">
          <h2 className="text-sm font-bold text-n8">{copy.timelineTitle}</h2>
          <ol className="mt-3 space-y-3 border-s-2 border-n3 ps-4">
            <li>
              <p className="text-sm font-semibold text-n8">{copy.timelineFiled}</p>
              <p className="text-2xs text-n6">{fmt.dateTime(request.createdAt)}</p>
            </li>
            {request.decidedAt ? (
              <li>
                <p className="text-sm font-semibold text-n8">{copy.timelineDecided}</p>
                <p className="text-2xs text-n6">
                  {fmt.dateTime(request.decidedAt)}
                  {request.deciderName ? ` · ${copy.timelineBy} ${request.deciderName}` : null}
                </p>
                {request.status === "rejected" && request.rejectReason ? (
                  <p className="mt-1 text-sm text-destructive">{request.rejectReason}</p>
                ) : null}
              </li>
            ) : null}
          </ol>
        </section>
      </div>
    </div>
  );
}
